/* eslint-disable max-statements */

import { Box, Button, Group, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { TSDViewConsignmentFormValues } from '@e-origin/contracts';
import { TransitHouseConsignment } from '@e-origin/shared';

import { SearchBox, Table } from '@components';
import { useSendDeconsolidation } from '@hooks/tsd';
import { EditHouse } from './EditHouse';
import { useHousesListColumns } from './useHousesListColumns';

interface HousesListProps {
  tsdName: string;
  consignmentForm: UseFormReturnType<TSDViewConsignmentFormValues>;
  disabled: boolean;
  transportDisabled: boolean;
  onSubmit: (values) => Promise<void>;
  showSendDeconsolidationButton: boolean;
}

export const HousesList: React.FC<HousesListProps> = ({ tsdName, consignmentForm, disabled, transportDisabled, onSubmit, showSendDeconsolidationButton }) => {
  const { id } = useParams<{ id: string }>() as any;
  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(new URLSearchParams(routeLocation.search));
  const navigate = useNavigate();

  const sendDeconsolidationMutation = useSendDeconsolidation();

  const [searchQuery, setSearchQuery] = useState(queryParams.current.get('houseSearch') ? queryParams.current.get('houseSearch') : '');
  const selectedRow = useRef<{ houseConsignmentIdx: number; houseConsigment: TransitHouseConsignment } | null>(null);

  const houseConsigments = useMemo(
    () =>
      (consignmentForm.values?.houseConsignment || []).filter((house) => {
        if (!searchQuery) {
          return true;
        }

        return house.consignmentItems?.find(
          (item) =>
            item.commodity?.descriptionOfGoods?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.commodity?.commodityCode?.harmonizedSystemSubHeadingCode?.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      }),
    [consignmentForm.values?.houseConsignment, searchQuery],
  );

  const handleSearch = (value: string) => {
    setSearchQuery(value);

    queryParams.current.set('houseSearch', value);
    navigate({ search: queryParams.current.toString() });
  };

  const handleSave = (houseConsignment, isMaster) => {
    if (isMaster) {
      const oldHouse = consignmentForm?.values?.master;
      consignmentForm.setFieldValue(`master`, {
        ...houseConsignment,
        consignmentItems: oldHouse?.consignmentItems || [],
      });
    } else {
      const houseConsignmentIdx = selectedRow.current?.houseConsignmentIdx ?? -1;
      if (houseConsignmentIdx >= 0) {
        const oldHouse = consignmentForm?.values?.houseConsignment[houseConsignmentIdx];
        consignmentForm.setFieldValue(`houseConsignment.${houseConsignmentIdx}`, {
          ...houseConsignment,
          consignmentItems: oldHouse.consignmentItems || [],
        });
      } else {
        consignmentForm.setFieldValue('houseConsignment', [...(consignmentForm?.values?.houseConsignment || []), houseConsignment]);
      }
    }

    selectedRow.current = null;
  };

  const handleAddNew = (isMaster) => {
    openHouseConsignment(null, isMaster);
  };

  const openHouseConsignment = (houseConsignmentIdx?, isMaster?) => {
    modals.open({
      title: isMaster ? 'Master consignment' : 'House consignment',
      size: 'xl',
      children: (
        <EditHouse
          consignmentForm={consignmentForm}
          houseConsignmentIdx={houseConsignmentIdx}
          isMaster={isMaster}
          onSave={(houseConsignment, isMaster) => handleSave(houseConsignment, isMaster)}
          onHide={modals.closeAll}
          disabled={disabled}
          transportDisabled={transportDisabled}
        />
      ),
    });
  };

  const handleSendDeconsolidation = async () => {
    await onSubmit(consignmentForm.values);
    sendDeconsolidationMutation.mutate(id);
  };

  const { columns, actionsColumn, masterActionsColumn } = useHousesListColumns({
    tsdName,
    consignmentForm,
    openHouseConsignment,
    queryParams,
    disabled,
    selectedRow,
  });

  return (
    <Stack>
      <Stack>
        <Group display={'flex'} py={12} justify="space-between" gap={10}>
          <Title order={3}>Master consignment</Title>
          {!disabled && !consignmentForm.values?.master && (
            <Button onClick={() => handleAddNew(true)} variant="outline" size="lg" data-pw="add-master-btn">
              Add master
            </Button>
          )}
        </Group>
        <Box mb={20}>
          <Table columns={[...columns, masterActionsColumn] as any} dataSource={consignmentForm.values?.master ? [consignmentForm.values?.master] : []} />
        </Box>
      </Stack>

      <Stack>
        <Group display={'flex'} py={12} justify="space-between" gap={10}>
          <Title order={3}>House consignments</Title>
          <Group display={'flex'}>
            {showSendDeconsolidationButton && (
              <Button
                onClick={() => handleSendDeconsolidation()}
                loading={sendDeconsolidationMutation.isPending}
                variant="outline"
                size="lg"
                data-pw="send-deconsolidation-btn"
              >
                Send deconsolidation
              </Button>
            )}
            <SearchBox handleSearch={handleSearch} value={searchQuery} placeholder="Search goods by HS code, description of goods" />
            {!disabled && (
              <Button onClick={() => handleAddNew(false)} variant="outline" size="lg" data-pw="add-house-btn">
                Add new
              </Button>
            )}
          </Group>
        </Group>
        <Box mb={20}>
          <Table key={searchQuery} columns={[...columns, actionsColumn] as any} dataSource={houseConsigments || []} pagination verticalSpacing="xs" />
        </Box>
      </Stack>
    </Stack>
  );
};
